import React, {type Dispatch, type SetStateAction, useCallback} from 'react';

import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';


import {PbEditorialSettingsSkeleton} from './pb-editorial-settings.skeleton';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {AnalystCommentarySettings} from 'src/openapi/admin/models/AnalystCommentarySettings';
import type {Error} from 'src/openapi/admin/models/Error';
import type {MarketMapSettings} from 'src/openapi/admin/models/MarketMapSettings';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    analystSettings: AnalystCommentarySettings,
    marketMapsSettings: MarketMapSettings,
    setAnalystSettings: Dispatch<SetStateAction<AnalystCommentarySettings>>,
    setMarketMapsSettings: Dispatch<SetStateAction<MarketMapSettings>>,
    onFieldChange: (parameterName: string) => void,
    disabled?: boolean,
};

const SECTION_NAME = 'Editorial Settings';

export const PbEditorialSettingsSection = (props: Props) => {
    const {hasAccess} = useAdminAccessRight();

    const {
        isLoading,
        loadingError,
        marketMapsSettings,
        analystSettings,
        setAnalystSettings,
        setMarketMapsSettings,
        onFieldChange,
        disabled,
    } = props;

    const changeMarketMapsSettings = useCallback(({event}: CheckBoxChangeData) => {
        setMarketMapsSettings({createMarketMapAllowed: event.currentTarget.checked});
        onFieldChange('Create PitchBook Market Maps and Verticals');
    }, [onFieldChange, setMarketMapsSettings]);

    const changeAnalystSettings = useCallback(({event}: CheckBoxChangeData) => {
        setAnalystSettings({commentaryOnDeals: event.currentTarget.checked});
        onFieldChange('Analyst Commentary on Deals');
    }, [onFieldChange, setAnalystSettings]);

    return (
        <Skeleton shown={isLoading} view={<PbEditorialSettingsSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.USER_SCREEN_VIEW as AccessRight}>
                <FormSection headline={SECTION_NAME}>
                    <CheckBox
                        disabled={!hasAccess(ACCESS_RIGHTS.CREATE_MARKET_MAP_VERTICALS) || disabled}
                        label="Create PitchBook Market Maps and Verticals"
                        checked={marketMapsSettings.createMarketMapAllowed}
                        onCheckBoxChange={changeMarketMapsSettings}
                    />
                    <CheckBox
                        disabled={!hasAccess(ACCESS_RIGHTS.ANALYST_COMMENTARY_DEALS) || disabled}
                        label="Analyst Commentary on Deals"
                        checked={analystSettings.commentaryOnDeals}
                        onCheckBoxChange={changeAnalystSettings}
                    />
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
